// assets/product-form.js
//
// A deliberate stub, NOT Dawn's ProductForm.
//
// Adding to cart happens in exactly one place in this theme: custom-quick-add.js
// intercepts the submit of every <product-form>'s form and does the AJAX add
// itself, so the toast and the header badge behave the same on the homepage and
// on a product page. Dawn's ProductForm was emptied out so it could not add a
// second listener and double every add.
//
// Emptying the file, though, also removed three members that product-info.js
// still calls the moment a variant is picked:
//
//   resetProductFormState()  -> toggleSubmitButton(), handleErrorMessage()
//   fetchQuantityRules()     -> variantIdInput
//
// With <product-form> an unknown element those calls threw before anything
// else ran, so choosing a size updated nothing — not the price, not the media,
// not the hidden id input. The radio looked selected (the browser does that on
// its own) while the form still carried the previously selected variant, and
// the cart quietly received the wrong one.
//
// This restores those three members and nothing else. No submit handling.
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      submitButtonText = undefined;

      connectedCallback() {
        // Captured once, from the markup, rather than read from
        // window.variantStrings on every re-enable: the section's own label may
        // differ from the translation, and variantStrings is not guaranteed to
        // exist on every template that renders a product form.
        if (this.submitButtonText === undefined) {
          this.submitButtonText = this.submitButtonLabel?.textContent ?? '';
        }
      }

      get form() {
        return this.querySelector('form');
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }

      get submitButton() {
        return this.querySelector('[type="submit"]');
      }

      // NOT querySelector('span'). The redesign puts a cart glyph in the first
      // span of the button:
      //
      //   <button>
      //     <span class="new-design-product__cart-icon"><svg…></span>
      //     <span>Add to Cart</span>
      //     <div class="loading__spinner">…</div>
      //   </button>
      //
      // Dawn's own ProductForm took the first span because stock Dawn has only
      // one. Here that is the glyph, and writing a label into it replaced the
      // <svg> with text — the icon vanished the moment a size was picked.
      //
      // The class is the intended hook; the fallback picks the first span that
      // holds text and no elements, so this still finds the right one on a
      // storefront running an older buy-buttons.liquid.
      get submitButtonLabel() {
        const button = this.submitButton;
        if (!button) return null;

        return (
          button.querySelector('.product-form__submit-label') ||
          Array.from(button.querySelectorAll('span')).find((span) => span.childElementCount === 0) ||
          null
        );
      }

      get errorMessageWrapper() {
        return this.querySelector('.product-form__error-message-wrapper');
      }

      toggleSubmitButton(disable = true, text) {
        const button = this.submitButton;
        if (!button) return;

        const label = this.submitButtonLabel;

        if (disable) {
          button.setAttribute('disabled', 'disabled');
          if (text && label) label.textContent = text;
        } else {
          button.removeAttribute('disabled');
          // Only put the original label back if something actually replaced it.
          // Writing unconditionally would blank the button on any theme where
          // the captured text came out empty.
          if (label && this.submitButtonText && label.textContent !== this.submitButtonText) {
            label.textContent = this.submitButtonText;
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        // The gift-card recipient form sets data-hide-errors and reports its
        // own validation, so it must not be overwritten from here.
        if (this.dataset.hideErrors === 'true') return;

        const wrapper = this.errorMessageWrapper;
        if (!wrapper) return;

        wrapper.toggleAttribute('hidden', !errorMessage);

        const target = wrapper.querySelector('.product-form__error-message');
        if (target) target.textContent = errorMessage || '';
      }
    }
  );
}
